import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const SKYSTAGE_MANAGED = 'SkyStage Managed';
const BYOG = 'BYOG (My Own GCP Project)';

/**
 * SecretsModePicker -- conditionally shows the Secrets Type radio buttons.
 *
 * **New deployments** (actionType is empty/undefined):
 *   The radio is hidden entirely. The field silently defaults to
 *   "SkyStage Managed" so the user goes straight to the key/value input.
 *
 * **Redeployments of existing SkyStage Managed apps** (actionType='redeploy',
 *   secretsMode pre-filled as 'SkyStage Managed'):
 *   The radio is hidden. The field outputs "SkyStage Managed".
 *
 * **Redeployments of existing BYOG apps** (actionType='redeploy',
 *   secretsMode pre-filled as 'BYOG (My Own GCP Project)'):
 *   The radio is shown so the user can keep BYOG or switch to SkyStage Managed.
 *
 * See IDP-972.
 */
export const SecretsModePicker = ({
  onChange,
  formData,
  formContext,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const contextFormData = formContext?.formData ?? {};
  const actionType = contextFormData.actionType as string | undefined;
  const isRedeploy = actionType === 'redeploy';

  // On redeploy the pre-filled secretsMode comes via formData (from the
  // template URL query string). On new deploy formData will be the schema
  // default ('SkyStage Managed').
  const prefilledMode = formData || SKYSTAGE_MANAGED;

  // Show the radio ONLY when redeploying an existing BYOG app.
  const showRadio = isRedeploy && prefilledMode === BYOG;

  // Stable ref to avoid triggering unnecessary re-renders from onChange identity changes
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // On mount, ensure the correct value is emitted.
  // For new deploys and SkyStage Managed redeploys this silently locks the
  // value to 'SkyStage Managed'. For BYOG redeploys the pre-filled value
  // is preserved.
  useEffect(() => {
    if (!showRadio) {
      onChangeRef.current(SKYSTAGE_MANAGED);
    } else {
      onChangeRef.current(prefilledMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showRadio) {
    // Hidden -- default to SkyStage Managed silently
    return <Box sx={{ display: 'none' }} />;
  }

  // BYOG redeploy -- render the radio so the user can switch if desired
  const title = schema?.title || 'Secrets Type';
  const description = schema?.description || '';

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup
          value={formData || SKYSTAGE_MANAGED}
          onChange={(_e, val) => onChange(val)}
        >
          <FormControlLabel
            value={SKYSTAGE_MANAGED}
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">
                <strong>SkyStage Managed</strong> <em>(Recommended)</em>
              </Typography>
            }
          />
          <FormControlLabel
            value={BYOG}
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">
                <strong>BYOG (My Own GCP Project)</strong>{' '}
                <em>(Legacy -- for existing apps only, will be deprecated)</em>
              </Typography>
            }
          />
        </RadioGroup>
        {description && (
          <FormHelperText sx={{ mt: 0.5, ml: 0 }}>{description}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
